import type { AudioSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, attemptAsync, round } from '../util/safe.js';
import { hash } from '../util/hash.js';

/**
 * Аудио-отпечаток.
 *
 * Одна и та же цепочка «осциллятор → компрессор», отрендеренная офлайн, даёт
 * чуть разные значения на разных реализациях WebAudio (округление в DSP,
 * версия сборки, SIMD-путь). Как и с canvas, важнее не сам хеш, а стабильность:
 * рандомизаторы шумят и здесь, ломая воспроизводимость внутри одной сессии.
 */

const SAMPLE_RATE = 44100;
const RENDERS = 3;

interface RenderResult {
  hash: string;
  sum: number;
}

type OfflineCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

function offlineCtor(): OfflineCtor | null {
  const g = globalThis as Record<string, unknown>;
  return (g['OfflineAudioContext'] ?? g['webkitOfflineAudioContext'] ?? null) as OfflineCtor | null;
}

async function renderOnce(Ctor: OfflineCtor): Promise<RenderResult | null> {
  return attemptAsync(async () => {
    const ctx = new Ctor(1, SAMPLE_RATE, SAMPLE_RATE);

    const oscillator = ctx.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.value = 10000;

    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;

    oscillator.connect(compressor);
    compressor.connect(ctx.destination);
    oscillator.start(0);

    const buffer = await ctx.startRendering();
    const data = buffer.getChannelData(0);

    // Окно после выхода компрессора на режим — здесь различия максимальны.
    let sum = 0;
    for (let i = 4500; i < 5000; i++) {
      sum += Math.abs(data[i]);
    }

    // Хешируем сырые байты среза, а не только сумму: сумма может совпасть
    // при разных формах сигнала.
    const slice = data.slice(4500, 5500);
    return { hash: hash(new Uint8Array(slice.buffer.slice(0))), sum: round(sum, 6) };
  });
}

function collectCompressorDefaults(): Record<string, number> | null {
  return attempt(() => {
    const Ctor = offlineCtor();
    if (!Ctor) return null;
    const ctx = new Ctor(1, 1, SAMPLE_RATE);
    const c = ctx.createDynamicsCompressor();
    return {
      threshold: c.threshold.defaultValue,
      knee: c.knee.defaultValue,
      ratio: c.ratio.defaultValue,
      attack: c.attack.defaultValue,
      release: c.release.defaultValue,
      reduction: round(c.reduction, 6),
      channelCount: c.channelCount,
      numberOfInputs: c.numberOfInputs,
      numberOfOutputs: c.numberOfOutputs,
    };
  });
}

export const audioModule = defineModule({
  id: 'audio',
  phase: 1,
  timeoutMs: 6000,
  async run(): Promise<AudioSignals> {
    const Ctor = offlineCtor();
    if (!Ctor) {
      return {
        supported: false,
        hash: null,
        sum: null,
        repeatHashes: null,
        stable: null,
        sampleRate: null,
        maxChannelCount: null,
        numberOfInputs: null,
        numberOfOutputs: null,
        channelCount: null,
        baseLatency: null,
        outputLatency: null,
        compressorDefaults: null,
      };
    }

    const results: RenderResult[] = [];
    for (let i = 0; i < RENDERS; i++) {
      const r = await renderOnce(Ctor);
      if (r) results.push(r);
    }

    const repeatHashes = results.map((r) => r.hash);

    // Живой AudioContext нужен только ради свойств устройства вывода;
    // сразу закрываем, чтобы не держать аудио-сессию.
    const live = attempt(() => {
      const g = globalThis as Record<string, unknown>;
      const AC = (g['AudioContext'] ?? g['webkitAudioContext']) as
        | (new () => AudioContext)
        | undefined;
      if (!AC) return null;
      const ctx = new AC();
      const info = {
        sampleRate: ctx.sampleRate,
        baseLatency: typeof ctx.baseLatency === 'number' ? round(ctx.baseLatency, 8) : null,
        outputLatency:
          typeof ctx.outputLatency === 'number' ? round(ctx.outputLatency, 8) : null,
        maxChannelCount: ctx.destination.maxChannelCount,
        numberOfInputs: ctx.destination.numberOfInputs,
        numberOfOutputs: ctx.destination.numberOfOutputs,
        channelCount: ctx.destination.channelCount,
      };
      void ctx.close();
      return info;
    });

    return {
      supported: true,
      hash: repeatHashes[0] ?? null,
      sum: results[0]?.sum ?? null,
      repeatHashes,
      stable: repeatHashes.length > 1 ? new Set(repeatHashes).size === 1 : null,
      sampleRate: live?.sampleRate ?? null,
      maxChannelCount: live?.maxChannelCount ?? null,
      numberOfInputs: live?.numberOfInputs ?? null,
      numberOfOutputs: live?.numberOfOutputs ?? null,
      channelCount: live?.channelCount ?? null,
      baseLatency: live?.baseLatency ?? null,
      outputLatency: live?.outputLatency ?? null,
      compressorDefaults: collectCompressorDefaults(),
    };
  },
});
